/*
 * Size priority & sorting.
 *
 * For every "Size" variant selector on the page (dropdown or button picker),
 * this module:
 *   1. Sorts the dropdown options smallest -> largest (by width, then length).
 *   2. Pre-selects a default size once per selector:
 *        - the first IN-STOCK size from the configured priority list, else
 *        - the smallest in-stock size, else
 *        - the smallest size overall.
 *
 * Config is provided by the theme via window.Eurus.sizeDefault:
 *   { enabled: Boolean, optionName: String, priority: [String, ...] }
 *
 * The sort runs on every (re)render via a MutationObserver, so it survives the
 * theme's fetch-and-swap variant updates. The default is applied only once per
 * selector instance (tracked with a flag on the persistent component root) and
 * is skipped on product pages opened with an explicit ?variant= deep link.
 *
 * Alpine gating: the theme's variant pickers react to change events through
 * Alpine listeners (@change / x-model) that only exist once Alpine has
 * initialized. Applying the default earlier checks the radio visually but the
 * change event is heard by nobody, so price / hidden id input / add-to-cart
 * keep the server-rendered first variant. Defaults on Alpine-managed pickers
 * are therefore deferred until alpine:initialized (without consuming the
 * once-per-section key), then applied so the theme's own handler runs.
 *
 * Anti-stomp protection is TWO independent mechanisms, because picker DOM
 * has two different lifecycles in this theme:
 *
 *   1. A WeakSet of processed picker nodes. Fresh content (a quick-view /
 *      choose-options popup injection, a theme-editor section reload, a
 *      second product in the same host section) is new nodes, so it gets
 *      its own default. Re-rendered content that REPLACES nodes (the
 *      theme's fetch-and-swap after a variant change) also reads as new —
 *      but those renders arrive showing the currently selected variant,
 *      so re-applying the default is a no-op unless someone changed the
 *      selection away from it. Storing this on the DOM (dataset) instead
 *      used to LOSE the flag on swap and re-apply the default over the
 *      size the shopper had just clicked, locking the page onto the
 *      default.
 *
 *   2. The someone who changes the selection is the shopper: a TRUSTED
 *      change/input event on any size input (synthetic events are not
 *      trusted) marks that section shopper-owned at module level, which
 *      survives every DOM swap. No default is ever applied there again.
 */
(function () {
  var hasWindow = typeof window !== 'undefined';
  if (hasWindow && window.__eurusSizePriorityLoaded) return;
  if (hasWindow) window.__eurusSizePriorityLoaded = true;

  // True once Alpine has walked the DOM and attached its listeners. If this
  // script is (re)injected after Alpine already started, the event was missed
  // but window.Alpine is set, so seed from that.
  var alpineReady = !!(hasWindow && window.Alpine);

  function insideAlpine(el) {
    return !!(el.closest && el.closest('[x-data]'));
  }

  function cfg() {
    return (hasWindow && window.Eurus && window.Eurus.sizeDefault) || null;
  }
  function isEnabled() {
    var c = cfg();
    return !!c && c.enabled !== false;
  }
  function optionName() {
    var c = cfg();
    return (c && c.optionName) || 'Size';
  }
  function priorities() {
    var c = cfg();
    var list = (c && c.priority) || [];
    return list.map(normSize).filter(Boolean);
  }

  // Normalize a size label for matching/parsing: lowercase, unify the
  // multiplication sign, strip foot/inch marks and quotes, drop whitespace.
  // "8' x 10'" / "8' × 10'" / "8 X 10" -> "8x10"
  function normSize(s) {
    return (s == null ? '' : String(s))
      .toLowerCase()
      .replace(/×/g, 'x') // × -> x
      .replace(/['"‘’“”′″]/g, '') // ' " ' ' " " ′ ″
      .replace(/\s+/g, '')
      .trim();
  }

  // Parse "W x L" into numeric feet. Returns null when not two numeric dims.
  function parseDims(value) {
    var parts = normSize(value).split('x');
    if (parts.length !== 2) return null;
    var w = parseFloat(parts[0]);
    var l = parseFloat(parts[1]);
    if (isNaN(w) || isNaN(l)) return null;
    return { w: w, l: l };
  }

  // Width ascending, then length ascending. Unparseable values sink to the end
  // (caller keeps original order among them for a stable result).
  function compareSizes(aVal, bVal) {
    var a = parseDims(aVal);
    var b = parseDims(bVal);
    if (a && b) return a.w !== b.w ? a.w - b.w : a.l - b.l;
    if (a) return -1;
    if (b) return 1;
    return 0;
  }

  function matchesSizeName(name) {
    if (!name) return false;
    var target = optionName().toLowerCase();
    var n = name.toLowerCase();
    // Dropdowns/radios use the raw option name ("Size"); combined-listing
    // radios suffix the position ("Size-1").
    return n === target || n.indexOf(target + '-') === 0;
  }

  function isProductPageDeepLink() {
    if (typeof location === 'undefined') return false;
    return /\/products\//.test(location.pathname) && /[?&]variant=/.test(location.search);
  }
  // Snapshot at load: applying a default makes the theme replaceState a
  // ?variant= param, which must not read as a deep link for pickers rendered
  // later on the same page (quick view, choose-option).
  var deepLinkAtLoad = isProductPageDeepLink();

  // ---- per-injection + shopper-intent memory ----
  var processedNodes = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  // pickerKey -> normSize of the value the shopper (or a deep link) chose.
  // Storing the VALUE, not a boolean, lets a later render tell the two
  // replacement cases apart: a fetch-swap echoing the shopper's choice
  // (leave it alone) versus pristine server HTML that already lost their
  // choice — a reopened quick-view popup is served from cache with the
  // first variant checked — where re-applying the merchant default beats
  // leaving the swatch selected.
  var userTouched = {};

  function alreadyProcessed(node) {
    if (!processedNodes || !node) return false;
    if (processedNodes.has(node)) return true;
    processedNodes.add(node);
    return false;
  }

  // Section identity survives the theme's fetch-and-swap re-renders (the
  // picker nodes, forms and fieldsets are all replaced wholesale). It is
  // not enough on its own: quick-view/choose-options popups for DIFFERENT
  // products share one section, and product-template.liquid wraps every
  // product rendering in id="x-product-template-{productId}-{sectionId}",
  // so the latch keys on section + product.
  function pickerKey(el) {
    var section =
      (el.closest && el.closest('[id^="shopify-section-"], section[id], [data-section-id]')) || null;
    var sid = section ? section.id || section.getAttribute('data-section-id') : 'page';
    var wrap = el.closest && el.closest('[id^="x-product-template-"]');
    return sid + '|' + (wrap ? wrap.id : '') + '|' + optionName().toLowerCase();
  }

  // A deep link's ?variant= is the shopper's choice for the product it
  // addresses — the MAIN product section ("…__main" is the Eurus product
  // template's section key). Popups and other product sections rendered on
  // that same page carry no such intent and still deserve their default.
  function deepLinkProtected(key) {
    return deepLinkAtLoad && key.indexOf('__main') !== -1;
  }

  // The shopper's own selection outranks any default while it is the live
  // selection. isTrusted filters out our fireChange and the theme's
  // synthetic events, so only human input claims a picker.
  function noteUserSelection(e) {
    if (!e.isTrusted) return;
    var t = e.target;
    if (!t || !t.name || !matchesSizeName(t.name)) return;
    if (t.tagName !== 'SELECT' && !(t.tagName === 'INPUT' && t.type === 'radio')) return;
    userTouched[pickerKey(t)] = normSize(t.value);
  }

  // One synthetic change per picker identity per pass: the theme re-renders
  // the whole product info on each change, so a second event for the same
  // target (main radios + sticky dropdown both defaulting) only doubles the
  // fetches and widens the stale-response window. Later pickers in the pass
  // are set visually and reconciled by the incoming render.
  var firedThisPass = {};

  // ---- sorting (dropdowns only — "the dropdown is sorted") ----
  function sortSelect(select) {
    var opts = Array.prototype.slice.call(select.options);
    if (opts.length < 2) return;
    var sorted = opts
      .map(function (el, i) {
        return { el: el, i: i };
      })
      .sort(function (A, B) {
        return compareSizes(A.el.value, B.el.value) || A.i - B.i;
      })
      .map(function (o) {
        return o.el;
      });

    var alreadySorted = opts.every(function (el, i) {
      return el === sorted[i];
    });
    if (alreadySorted) return;

    var selectedValue = select.value; // detaching options can drop selection
    var frag = document.createDocumentFragment();
    sorted.forEach(function (el) {
      frag.appendChild(el);
    });
    select.appendChild(frag);
    if (select.value !== selectedValue) select.value = selectedValue; // restore
  }

  // ---- default selection ----
  // items: [{ el, value, available }]. Order-independent on purpose: radio
  // groups are never sorted in the DOM, so "first item" is whatever the
  // template rendered first — on products without a priority size that used
  // to be the swatch, the very thing this module exists to steer away from.
  function pickDesired(items) {
    if (!items.length) return null;
    var prios = priorities();
    for (var p = 0; p < prios.length; p++) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].available && normSize(items[i].value) === prios[p]) return items[i];
      }
    }
    var sized = items.filter(function (it) {
      return parseDims(it.value); // real W x L sizes only — never a swatch
    });
    var inStock = sized.filter(function (it) {
      return it.available;
    });
    var pool = inStock.length ? inStock : sized; // all sold out -> smallest size, shown as such
    if (!pool.length) return null; // swatch-only listing: leave the server default alone
    return pool.sort(function (a, b) {
      return compareSizes(a.value, b.value);
    })[0];
  }

  function fireChange(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyDefaultToSelect(select) {
    var key = pickerKey(select);
    if (userTouched[key] && userTouched[key] === normSize(select.value)) return; // their choice is live
    if (insideAlpine(select) && !alpineReady) return; // retry after alpine:initialized
    if (alreadyProcessed(select)) return;
    if (deepLinkProtected(key)) {
      userTouched[key] = normSize(select.value); // the link chose; renders echoing it stay theirs
      return;
    }

    var items = Array.prototype.slice.call(select.options).map(function (o) {
      return { el: o, value: o.value, available: o.dataset.available !== 'false' };
    });
    var chosen = pickDesired(items);
    if (!chosen || select.value === chosen.el.value) return;

    items.forEach(function (it) {
      it.el.removeAttribute('selected');
    });
    chosen.el.setAttribute('selected', 'selected');
    chosen.el.selected = true;
    select.value = chosen.el.value;
    if (!firedThisPass[key]) {
      firedThisPass[key] = true;
      fireChange(select);
    }
  }

  function applyDefaultToRadios(group) {
    if (!group.length) return;
    var key = pickerKey(group[0]);
    var checkedNow = group.filter(function (r) {
      return r.checked;
    })[0];
    if (userTouched[key] && checkedNow && userTouched[key] === normSize(checkedNow.value)) return; // their choice is live
    if (insideAlpine(group[0]) && !alpineReady) return; // retry after alpine:initialized
    // Track the group's container: the radios themselves are re-created on
    // some partial renders while the container survives, and vice versa on
    // full swaps — either way a genuinely fresh injection is a new container.
    var container = group[0].closest('fieldset') || group[0].form || group[0].parentElement;
    if (alreadyProcessed(container)) return;
    if (deepLinkProtected(key)) {
      // The link chose; renders echoing it stay theirs.
      userTouched[key] = checkedNow ? normSize(checkedNow.value) : ' none';
      return;
    }

    var items = group.map(function (r) {
      return {
        el: r,
        value: r.value,
        available: r.dataset.available !== 'false' && !r.classList.contains('disabled'),
      };
    });
    var chosen = pickDesired(items);
    if (!chosen || chosen.el.checked) return;

    // Keep the checked ATTRIBUTE in sync too: the theme seeds Alpine state
    // from `input[checked]` (attribute selector) on (re)init.
    items.forEach(function (it) {
      it.el.removeAttribute('checked');
    });
    chosen.el.setAttribute('checked', 'checked');
    chosen.el.checked = true;
    if (!firedThisPass[key]) {
      firedThisPass[key] = true;
      fireChange(chosen.el);
    }
  }

  function processRadios(scope) {
    var radios = Array.prototype.slice
      .call(scope.querySelectorAll('input[type="radio"]'))
      .filter(function (r) {
        return matchesSizeName(r.name);
      });
    if (!radios.length) return;

    var processed = [];
    radios.forEach(function (r) {
      var container = r.closest('fieldset') || r.form || r.parentElement;
      var already = processed.some(function (g) {
        return g.container === container && g.name === r.name;
      });
      if (already) return;
      processed.push({ container: container, name: r.name });
      var group = radios.filter(function (x) {
        return (x.closest('fieldset') || x.form || x.parentElement) === container && x.name === r.name;
      });
      applyDefaultToRadios(group);
    });
  }

  function process(scope) {
    if (!isEnabled()) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice.call(root.querySelectorAll('select')).forEach(function (select) {
      if (!matchesSizeName(select.name)) return;
      sortSelect(select);
      applyDefaultToSelect(select);
    });
    // A scoped node may itself be a select (e.g. injected directly).
    if (root.matches && root.matches('select') && matchesSizeName(root.name)) {
      sortSelect(root);
      applyDefaultToSelect(root);
    }
    processRadios(root);
  }

  function runProcess() {
    firedThisPass = {};
    try {
      process(document);
    } catch (e) {
      /* never let selector logic break the page */
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // setTimeout (not rAF) so the pass still runs in background/inactive tabs.
    setTimeout(function () {
      scheduled = false;
      runProcess();
    }, 0);
  }

  function init() {
    runProcess(); // immediate first pass (DOM is parsed for a deferred script)
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) {
            schedule();
            return;
          }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
    document.addEventListener('alpine:initialized', function () {
      alpineReady = true;
      schedule();
    });
    // Capture phase: the claim must land even if the theme stops propagation.
    document.addEventListener('change', noteUserSelection, true);
    document.addEventListener('input', noteUserSelection, true);
  }

  // Exposed for unit testing under Node; harmless in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      normSize: normSize,
      parseDims: parseDims,
      compareSizes: compareSizes,
      matchesSizeName: matchesSizeName,
      pickDesired: pickDesired,
      sortSelect: sortSelect,
      applyDefaultToSelect: applyDefaultToSelect,
      applyDefaultToRadios: applyDefaultToRadios,
      isProductPageDeepLink: isProductPageDeepLink,
      process: process,
      pickerKey: pickerKey,
      noteUserSelection: noteUserSelection,
      _setAlpineReady: function (v) {
        alpineReady = !!v;
      },
      _state: function () {
        return { userTouched: userTouched };
      },
      _reset: function () {
        userTouched = {};
        firedThisPass = {};
        if (typeof WeakSet !== 'undefined') processedNodes = new WeakSet();
      },
    };
  }
})();
